import React, { useRef, useEffect, useState } from 'react';
import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax

import { useQuery } from '@tanstack/react-query';

import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';

import { getAllDevices } from 'api/bks';

import 'mapbox-gl/dist/mapbox-gl.css';

// props-types is a library for typechecking of props
import PropTypes from "prop-types";

import Card from "@mui/material/Card";
import Box from '@mui/material/Box';

import { center, bbox, bboxPolygon } from "@turf/turf";
import geoViewport from "@mapbox/geo-viewport";

mapboxgl.accessToken = 'pk.eyJ1IjoiaGFycnltb250YW5vIiwiYSI6ImNqZWY0bDcyaTFkcGYzM28ydHFpaDJxaWcifQ.Wf7aNVePBRWZ9fMy_I81qw';

function setFeatures(group) {
    var features = group.geojson;
    // console.log(group);

    features.forEach(function (feature) {
        feature.properties = {
            group_id: group.group_id,
            name: group.name,
            parent_id: group.parent_id
            // uuids: group.uuids
        };
    });

    return features;
}

function setDeviceFeatures(device) {

    var features = [
        {
            type: "Feature",
            properties: {
                name: device.name,
                uuid: device.uuid
            },
            geometry: {
                type: 'Point',
                coordinates: device.position
            }
        }
    ];

    return features;
}

function EditDeviceMap({ group, selected_device, padding }) {

    const mapContainer = useRef(null);
    const map = useRef(null);

    useEffect(() => {
        if (map.current) {
            return;
        }

        var bounds = bbox({
            "type": "FeatureCollection",
            "features": group.geojson
        });

        var polygon = bboxPolygon(bounds);

        // console.log(polygon)
        var map_settings = {
            style: 'mapbox://styles/harrymontano/cm01omrnz000u01omf2uegk93?optimize=true',
            zoom: 14,
        };

        map_settings.container = mapContainer.current;
        map_settings.center = center({
            "type": "FeatureCollection",
            "features": group.geojson
        }).geometry.coordinates;

        // console.log(map_settings)
        map.current = new mapboxgl.Map(map_settings);

        map.current.fitBounds(polygon.bbox, {
            linear: true,
            padding: padding,
            duration: 1500
        });

    }, []);

    useEffect(() => { //mount
        if (map.current) {

            map.current.on('load', () => {
                var existing_base_layer = map.current.getLayer('geojson-base');
                var existing_base_source = map.current.getSource('geojson-base');

                if (!existing_base_source) {
                    map.current.addSource('geojson-base', {
                        type: 'geojson',
                        data: {
                            "type": "FeatureCollection",
                            "features": setFeatures(group)
                        }
                    });
                }
                if (!existing_base_layer) {
                    map.current.addLayer({
                        id: 'geojson-base',
                        type: 'fill',
                        source: 'geojson-base',
                        paint: {
                            "fill-opacity": 0.1,
                            "fill-color": "black"
                        }
                    }).addLayer({
                        id: 'geojson-border-base',
                        type: 'line',
                        source: 'geojson-base',
                        paint: {
                            "line-color": "black",
                            "line-width": 2,
                            "line-opacity": 0.2
                        }
                    });

                    // map.current.on('click', 'geojson-base', handleGroupClick);
                }

                if (group.subgroups.length > 0) {
                    group.subgroups.forEach(function (subgroup) {
                        if (subgroup.group_id != group.group_id) {
                            var existing_group_layer = map.current.getLayer('geojson-' + subgroup.group_id);
                            var existing_group_source = map.current.getSource('geojson-' + subgroup.group_id);

                            if (!existing_group_source) {
                                map.current.addSource('geojson-' + subgroup.group_id, {
                                    type: 'geojson',
                                    data: {
                                        "type": "FeatureCollection",
                                        "features": setFeatures(subgroup)
                                    }
                                });
                            }
                            if (!existing_group_layer) {
                                map.current.addLayer({
                                    id: 'geojson-' + subgroup.group_id,
                                    type: 'line',
                                    source: 'geojson-' + subgroup.group_id,
                                    paint: {
                                        "line-color": "black",
                                        "line-width": 4,
                                        "line-opacity": 0.4
                                    }
                                });
                            }
                        }
                    })

                    // var existing_selected_group_layer = map.current.getLayer('geojson-' + group.group_id);
                    // var existing_selected_group_source = map.current.getSource('geojson-' + group.group_id);

                    // if (!existing_selected_group_source) {
                    //     map.current.addSource('geojson-' + group.group_id, {
                    //         type: 'geojson',
                    //         data: {
                    //             "type": "FeatureCollection",
                    //             "features": setFeatures(group)
                    //         }
                    //     });
                    // }
                    // if (!existing_selected_group_layer) {
                    //     map.current.addLayer({
                    //         id: 'geojson-' + group.group_id,
                    //         type: 'line',
                    //         source: 'geojson-' + group.group_id,
                    //         paint: {
                    //             "line-color": group.all_updated ? "#58ba2d" : "#ff667c",
                    //             "line-width": 4,
                    //             "line-opacity": 0.4
                    //         }
                    //     });
                    // }
                }

                group.devices.forEach(function (device) {
                    var existing_device_layer = map.current.getLayer('geojson-' + device.uuid);
                    var existing_device_source = map.current.getSource('geojson-' + device.uuid);

                    // console.log(selected_device)

                    if (selected_device.uuid == device.uuid) {
                        var colour = device.connectivity == 'online' ? "#58ba2d" : "#ff667c";
                    }
                    else {
                        var colour = 'grey';
                    }

                    // console.log(device);

                    if (!existing_device_source) {
                        map.current.addSource('device-' + device.uuid, {
                            type: 'geojson',
                            data: {
                                "type": "FeatureCollection",
                                "features": setDeviceFeatures(device)
                            }
                        });
                    }
                    if (!existing_device_layer) {
                        map.current.addLayer({
                            id: 'device-' + device.uuid,
                            type: 'circle',
                            source: 'device-' + device.uuid,
                            paint: {
                                'circle-color': colour,
                                'circle-radius': 9,
                                'circle-stroke-width': 3,
                                'circle-stroke-color': '#ffffff'
                            }
                        });

                        // map.current.on('click', 'device-' + device.uuid, handleDeviceClick);
                        // map.current.on('mouseenter', 'device-' + device.uuid, handleMouseEnter);
                        // map.current.on('mouseleave', 'device-' + device.uuid, handleMouseLeave);
                    }
                });

                // map.current.on('click', handleMapClick);
            });
        }
    }, []);

    useEffect(() => () => { //unmount

        var existing_base_layer = map.current.getLayer('geojson-base');
        if (existing_base_layer) {
            map.current.removeLayer('geojson-base');
            map.current.removeLayer('geojson-border-base');
            map.current.removeSource('geojson-base');

            // map.current.off('click', 'geojson-base', handleGroupClick);
        }

        if (group.subgroups.length > 0) {
            group.subgroups.forEach(function (subgroup) {
                var existing_group_layer = map.current.getLayer('geojson-' + subgroup.group_id);
                if (existing_group_layer) {
                    map.current.removeLayer('geojson-' + subgroup.group_id);
                    map.current.removeSource('geojson-' + subgroup.group_id);
                }
            });

            // var existing_selected_group_layer = map.current.getLayer('geojson-' + group.group_id);
            // if (existing_selected_group_layer) {
            //     map.current.removeLayer('geojson-' + group.group_id);
            //     map.current.removeSource('geojson-' + group.group_id);
            // }
        }

        group.devices.forEach(function (device) {
            var existing_device_layer = map.current.getLayer('device-' + device.uuid);
            // console.log(existing_device_layer);
            if (existing_device_layer) {
                map.current.removeLayer('device-' + device.uuid);
                map.current.removeSource('device-' + device.uuid);

                // map.current.off('click', 'device-' + device.uuid, handleDeviceClick);
                // map.current.off('mouseenter', 'device-' + device.uuid, handleMouseEnter);
                // map.current.off('mouseleave', 'device-' + device.uuid, handleMouseLeave);
            }
        });

        // map.current.off('click', handleMapClick);

    }, []);

    return (
        <Box component="div" ref={mapContainer} sx={{ height: '100%' }} />
    );
}

EditDeviceMap.propTypes = {
    group: PropTypes.object,
    selected_device: PropTypes.object,
    padding: PropTypes.number
};

export default EditDeviceMap;